import * as React from 'react';
import { cva, type VariantProps } from 'class-variance-authority';
import { cn } from '@/lib/utils';

const badgeVariants = cva(
  'inline-flex items-center rounded-full border px-2.5 py-0.5 text-xs font-semibold transition-colors focus:outline-none focus:ring-2 focus:ring-emerald-500 focus:ring-offset-2 focus:ring-offset-bg-primary',
  {
    variants: {
      // Uniform "inverted" label style (matches candidate tags / quality badge):
      // colored text + colored border on a dark tinted background.
      variant: {
        default: 'border-emerald-500/30 bg-emerald-500/15 text-emerald-400 hover:bg-emerald-500/25',
        secondary: 'border-white/20 bg-white/10 text-text-secondary hover:bg-white/15',
        destructive: 'border-red-500/30 bg-red-500/15 text-red-400 hover:bg-red-500/25',
        outline: 'border-white/20 text-text-secondary',
        success: 'border-green-500/30 bg-green-500/15 text-green-400 hover:bg-green-500/25',
        warning: 'border-amber-500/30 bg-amber-500/15 text-amber-400 hover:bg-amber-500/25',
        info: 'border-blue-500/30 bg-blue-500/15 text-blue-400 hover:bg-blue-500/25',
      },
    },
    defaultVariants: {
      variant: 'default',
    },
  },
);

export interface BadgeProps
  extends React.HTMLAttributes<HTMLDivElement>,
    VariantProps<typeof badgeVariants> {}

const Badge = React.forwardRef<HTMLDivElement, BadgeProps>(
  ({ className, variant, ...props }, ref) => {
    return <div ref={ref} className={cn(badgeVariants({ variant }), className)} {...props} />;
  },
);
Badge.displayName = 'Badge';

export { Badge, badgeVariants };
